/**
 * Editing a matter's own details.
 *
 * Only the fields a matter genuinely owns: what it is called, what it is about,
 * how urgent it is, and when it is expected to finish. Everything else on the
 * screen belongs to something else and is changed where it lives —
 *
 *   stage      through the workflow, which enforces the guards
 *   status     a consequence of the stage, never set directly
 *   client     a matter is not moved between clients; it is opened again
 *   service    it carries the workflow, so changing it would change the rules
 *              a matter is already part-way through
 *
 * The server agrees: every one of those is read-only on the serializer, so a
 * form offering them would be lying about what it could do.
 */

import { STAFF, qk, useApi, useApiMutation, type CaseDetail } from '@legalcrm/api';
import {
  Form,
  FormActions,
  FormDate,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  useZodForm,
} from '@legalcrm/ui';
import { z } from 'zod';

const schema = z.object({
  title: z.string().min(1, 'A matter needs a title'),
  description: z.string(),
  priority: z.string(),
  target_completion_date: z.string(),
});

type Values = z.infer<typeof schema>;

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export interface EditCaseModalProps {
  open: boolean;
  onClose: () => void;
  caseRecord: CaseDetail;
}

export function EditCaseModal({ open, onClose, caseRecord }: EditCaseModalProps) {
  const api = useApi();

  const form = useZodForm(schema, {
    values: {
      title: caseRecord.title ?? '',
      description: (caseRecord as { description?: string }).description ?? '',
      priority: (caseRecord as { priority?: string }).priority ?? 'normal',
      target_completion_date:
        (caseRecord as { target_completion_date?: string }).target_completion_date ?? '',
    },
  });

  const save = useApiMutation<CaseDetail, Values>({
    mutationFn: (values) =>
      api.patch<CaseDetail>(
        STAFF.case(caseRecord.id),
        {
          title: values.title,
          description: values.description,
          priority: values.priority,
          target_completion_date: values.target_completion_date || null,
        },
        // Optimistic concurrency: if somebody else changed this matter since the
        // screen loaded, this is refused rather than quietly overwriting them.
        { ifMatch: (caseRecord as { version?: number }).version },
      ),
    invalidates: [qk.detail('cases', caseRecord.id), qk.all('cases')],
  });

  return (
    <Modal open={open} onClose={onClose} title="Edit matter">
      <Form
        form={form}
        ariaLabel="Edit matter"
        error={save.error}
        onSubmit={async (values) => {
          await save.mutateAsync(values);
          onClose();
        }}
      >
        <FormInput form={form} name="title" label="Title" autoFocus />

        <FormTextarea
          form={form}
          name="description"
          label="Background"
          rows={4}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormSelect
            form={form}
            name="priority"
            label="Priority"
            options={PRIORITIES}
          />
          <FormDate
            form={form}
            name="target_completion_date"
            label="Target completion"
          />
        </div>

        <FormActions submitLabel="Save" busy={save.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}
